import { useAppSelector, useAppDispatch } from "@/services/hooks";
import React, { useEffect, useMemo, useState } from "react";

import {
  addSite,
  clearMessage,
  restoreSite,
  releaseSite,
} from "@/services/siteServices/siteActions";
import { setAppMessage } from "@/services/appMessageServices/appMessageActions";
import { getAll } from "@/services/designServices/designAction";
import { useImageUpload } from "@/services/ImageServices/useImageUpload";
import { setErrors } from "@/services/errorServices/errorAction";

import {
  COUNTRY_OPTIONS,
  LANGUAGE_OPTIONS,
} from "@/utils/selectOptions";

import SimpleHeader from "@/components/Headers/SimpleHeader";
import AddNewSiteView from "./AddNewSiteView";
import { asCaught } from "@/services/caughtError";

/** What ImageUploadContainer hands back: a picked file, or a media-library pick. */
type PickedImage = {
  data?: string;
  imgName?: string;
  secure_url?: string;
  context?: unknown;
};

/** The Add New Site form, which is also the POST /api/sites payload. */
type SiteForm = {
  name: string;
  domain: string;
  canonial_url: string;
  country_code: string;
  language_code: string;
  design: { _id: string; name: string; source: string };
  logo_pic: string;
  active: boolean;
  analytics_code: string;
  gtm: string;
  special_style_files: string;
  special_script_files: string;
};

/** SCRUM-1817: the soft-deleted site a 409 named, and the payload that got the 409. */
type DeletedHolder = { id: string; msg: string; value: string; payload: SiteForm };

const AddNewSiteContainer = () => {
  const dispatch = useAppDispatch();
  const uploadImage = useImageUpload();

  const designsFromStore = useAppSelector((s) => s.designs.designs || []);
  const sitesSlice = useAppSelector((s) => s.sites);

  const [loading, setLoading] = useState(false);
  const [logoPic, setLogoPic] = useState<PickedImage | null>(null);
  // SCRUM-1817: the soft-deleted site a 409 named, plus the payload that got
  // the 409 — Start fresh re-sends exactly that (logo already uploaded).
  const [deletedHolder, setDeletedHolder] = useState<DeletedHolder | null>(null);
  const [site, setSite] = useState<SiteForm>({
    name: "",
    domain: "",
    canonial_url: "",
    country_code: "",
    language_code: "",
    design: { _id: "", name: "", source: "" },
    logo_pic: "",
    active: false,
    analytics_code: "",
    gtm: "",
    special_style_files: "",
    special_script_files: "",
  });

  const designOptions = useMemo(
    () =>
      (designsFromStore || []).map((d) => ({
        value: { _id: d._id },
        label: d.name,
      })),
    [designsFromStore]
  );

  // load designs
  useEffect(() => {
    dispatch(getAll());
  }, [dispatch]);

  // Errors are surfaced by the shell hub (GlobalNotifications) from state.errors
  // (SCRUM-989 removed this view's local toast); a rejected addSite resets the
  // loading flag in submitSite below, not in an effect that mirrors state.errors.

  useEffect(() => {
    if (sitesSlice.message) {
      // Route the success through the shell hub (SCRUM-883) instead of a local
      // default ToastContainer; the form reset below stays driven by this view's
      // own sites.message signal.
      dispatch(setAppMessage("success", sitesSlice.message));
      dispatch(clearMessage());
      setLoading(false);
      setSite({
        name: "",
        domain: "",
        canonial_url: "",
        country_code: "",
        language_code: "",
        design: { _id: "", name: "", source: "" },
        logo_pic: "",
        active: false,
        analytics_code: "",
        gtm: "",
        special_style_files: "",
        special_script_files: "",
      });
      setLogoPic(null);
    }
  }, [sitesSlice.message, dispatch]);

  // handlers
  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value, type, checked } = e.target;
    setSite((s) => ({ ...s, [name]: type === "checkbox" ? checked : value }));
  };

  const onChangeSelect = (option: { value: string } | null, name: string) => {
    setSite((s) => ({ ...s, [name]: option ? option.value : "" }));
  };

  const onChangeDesign = (option: { value: { _id: string } } | null) => {
    setSite((s) => ({
      ...s,
      design: { ...s.design, _id: option ? option.value._id : "" },
    }));
  };

  // addSite surfaces every failure through the hub (GET_ERRORS) and rejects;
  // the one rejection this view acts on is the SCRUM-1817 409, which names the
  // deleted site that still holds the domain or canonical URL.
  const submitSite = async (payload: SiteForm) => {
    setDeletedHolder(null);
    try {
      await dispatch(addSite(payload)).unwrap();
    } catch (err) {
      setLoading(false);
      const first = asCaught(err)?.response?.data?.errors?.[0];
      if (asCaught(err)?.response?.status === 409 && first?.deletedId) {
        setDeletedHolder({
          id: String(first.deletedId),
          msg: String(first.msg ?? "A deleted site holds this value"),
          value: String(first.value ?? ""),
          payload,
        });
      }
    }
  };

  const onRestore = async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(restoreSite(deletedHolder.id)).unwrap();
    } catch {
      return; // surfaced via GET_ERRORS; the offer stays on screen
    }
    setDeletedHolder(null);
    dispatch(setAppMessage("success", "The deleted site is restored, with its pages"));
  };

  const onStartFresh = async () => {
    if (!deletedHolder) return;
    try {
      await dispatch(releaseSite(deletedHolder.id)).unwrap();
    } catch {
      return; // surfaced via GET_ERRORS; the offer stays on screen
    }
    await submitSite(deletedHolder.payload);
  };

  const onClickAdd = async (e: React.MouseEvent) => {
    e.preventDefault();
    if (logoPic?.data) {
      const path = `/${site.country_code}/SITES/${logoPic.imgName}`;
      setLoading(true);
      // Linearised (SCRUM-1021): upload via the shared useImageUpload hook, then
      // save inline with the returned URL — no more state.images.url watch-effect
      // (the old success-only effect left Add doing nothing, with no toast, on a
      // failed upload). This view surfaces errors only through the shell hub
      // (SCRUM-989 removed its local toast), so push a failed upload into
      // state.errors for the hub and clear the spinner.
      try {
        const url = await uploadImage(logoPic.data, path);
        await submitSite({ ...site, logo_pic: url });
      } catch (err) {
        setLoading(false);
        dispatch(
          setErrors({
            message:
              asCaught(err)?.message || "Uploading the logo failed. Please try again.",
          })
        );
      }
    } else if (logoPic?.secure_url) {
      await submitSite({ ...site, logo_pic: logoPic.secure_url });
    } else {
      await submitSite(site);
    }
  };

  return (
    <>
      <SimpleHeader name="Add New Site" parentName="Sites" />
      <AddNewSiteView
        site={site}
        loading={loading}
        countryOptions={COUNTRY_OPTIONS}
        languageOptions={LANGUAGE_OPTIONS}
        designOptions={designOptions}
        onChange={onChange}
        onChangeSelect={onChangeSelect}
        onChangeDesign={onChangeDesign}
        onClickAdd={onClickAdd}
        setLogoPic={setLogoPic}
        deletedHolder={deletedHolder}
        onRestore={onRestore}
        onStartFresh={onStartFresh}
      />
    </>
  );
};

export default AddNewSiteContainer;
